import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { ButtonDisabledOrLoader } from '../../../../../../../../../../baseComponents/Button'
import { TwinIconToolTip } from '../../../../../../../../../../baseComponents/TwinIcon'
import { OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { twinFetchPostText } from '../../../../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../../../../utils/globals/link'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { useLocation, useNavigate } from 'react-router'
import PermissionChecker from '../../../../../../../../../../baseComponents/PermissionChecker'
import { getUserInfo, getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import useEditDeleteModal from '../../../../../../../../../../utils/hooks/useEditDeleteModal'
import ModalDeleteBuyOrderDraft from './ModalDeleteBuyOrderDraft'
import { checkBuyOrderDraftAllOk } from '../../../../../../../../../../utils/globals/buy/buy'
import {  BuyDownloadButtonCSV, BuyDownloadButtonPDF, BuyDownloadPdfTemplateButton, BuyDownloadTags, BuyPdfPreview, BuySupplierPdfPreview } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/BuyButtons'
import { BuyOrderDuplicateButton } from '../../../../BuyOrderButtons'
import { ButtonOrderInfoLeftBase, ButtonOrderInfoLeftBaseProps, OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import useIsLoading from '../../../../../../../../../../utils/hooks/useIsLoading'

const BuyOrderCreateStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageBuy } = useBuyOrderCreateStepButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useBuyOrderCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <BuyOrderCreateButton  title={t('processBuyOrder', 'Procesar compra')} disabled={infoMessageBuy ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageBuy || ''} />
        </div>
    )
}

const useBuyOrderCreateStepButtonsLogic = ({ data, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    let infoMessageBuy = checkBuyOrderDraftAllOk(data, t)
    const Order = data?.header
    const name = 'buy_order_create_step_buttons'
    const userInfo = getUserInfo()
    const EmployeeId = userInfo?.Employee?.id
    const orderEmployeeId = Order?.CreateEmployeeId
    const isMyBuy = orderEmployeeId === EmployeeId

    const defaultButtons = [{ "id": "previewBuyOrderPDF" }, { "id": "downloadBuy" }, { "id": "duplicateBuyOrder" }, { "id": "deleteBuyOrder" }]

    const buyOrderDuplicateText = t('duplicateBuyOrder', 'Duplicar pedido de compra')
    const buyOrderDeleteText = t('deleteDraft', 'Eliminar borrador')
    const buyOrderDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const buyOrderDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const buyOrderPreviewPDFText = t('previewPdfTemplate', 'Previsualizar plantilla PDF')
    const buyOrderPreviewSupplierPDFText = t('previewSupplierPDF', 'Previsualizar PDF proveedor')
    const buyOrderDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const buyOrderDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const buyOrderDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const buyOrderVariablesText = t('variables', 'Variables')
    const buyOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateBuyOrder: {
                component: BuyOrderDuplicateButton,
                extraProps: {
                    title: buyOrderDuplicateText,
                    id: 'duplicateBuyOrder',
                    allRowData: data,
                    permissionTy: 'buy.order',
                }
            },
            deleteBuyOrder: {
                component: BuyOrderDeleteButton,
                showButton: isMyBuy,
                extraProps: {
                    title: buyOrderDeleteText,
                    id: 'deleteBuyOrder',
                    allRowData: data,
                }
            },
            downloadBuyCsv: {
                component: BuyDownloadButtonCSV,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyOrderDownloadCSVText,
                    id: 'downloadBuyCsv',
                    Order,
                    typeTemplate:'buyOrder'
                }
            },
            downloadBuyTag: {
                component: BuyDownloadTags,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyOrderDownloadTagsText,
                    id: 'downloadBuyTag',
                    Order,
                    typeTemplate:'buyOrder'
                }
            },
            previewBuySupplierPDF: {
                component: BuySupplierPdfPreview,
                showButton: Order.supplierPdf?.length,
                extraProps: {
                    title: buyOrderPreviewSupplierPDFText,
                    id: 'previewBuyOrderPDFSupplier',
                    Order,
                    typeTemplate: 'buyOrder',
                    key: 'previewBuyOrderPDFSupplier'
                }
            },
            previewBuyOrderPDF: {
                component: BuyPdfPreview,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyOrderPreviewPDFText,
                    id: 'previewBuyOrderPDF',
                    Order,
                    typeTemplate: 'buyOrder',
                    key: 'previewBuyOrderPDF'
                }
            },
            downloadBuyTemplate: {
                component: BuyDownloadPdfTemplateButton,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyOrderDownloadPDFTemplateText,
                    id: 'downloadBuyTemplate',
                    Order,
                    typeTemplate: 'buyOrder',
                    key: 'downloadBuyPDFTemplate'
                }
            },
            downloadBuy: {
                component: BuyDownloadButtonPDF,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyOrderDownloadPDFText,
                    id: 'downloadBuy',
                    Order,
                    typeTemplate: 'buyOrder',
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: buyOrderDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: buyOrderVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            buyProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: buyOrderProductTypesText,
                    id: 'buyProductTypes',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
        }
    }, [Order, buyOrderDeleteText, buyOrderDivideIntoChaptersText, buyOrderDownloadCSVText, buyOrderDownloadPDFTemplateText, buyOrderDownloadPDFText, buyOrderDownloadTagsText, buyOrderDuplicateText, buyOrderVariablesText, data, isMyBuy, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions, buyOrderPreviewPDFText, infoMessageBuy, buyOrderProductTypesText, buyOrderPreviewSupplierPDFText])

    return { infoMessageBuy, name, ButtonComponents, defaultButtons }
}

export const BuyOrderCreateStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageBuy } = useBuyOrderCreateStepButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useBuyOrderCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <BuyOrderCreateButton title={t('processBuyOrder', 'Procesar compra')} disabled={infoMessageBuy ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageBuy || ''} />
        </div>
    )
}

type BuyOrderCreateButtonProps = BuyOrderCreateButtonLogicProps & ButtonOrderInfoLeftBaseProps & {
    title: string
    children?: React.ReactNode
}

const BuyOrderCreateButton: React.FC<BuyOrderCreateButtonProps> = ({ title, OrderId, onSubmit, status, disabled, infoMessage, ...rest }) => {
    const { finalizeOrder, loading } = useBuyOrderCreateButtonLogic({ OrderId, onSubmit, status })
    return (
        <ButtonOrderInfoLeftBase disabled={disabled} infoMessage={infoMessage} {...rest}>
            <div className='min-w-175'>
                <ButtonDisabledOrLoader onClick={finalizeOrder} buttonIsDisabled={disabled} textButton={title} loading={loading} className='w-full' />
            </div>
        </ButtonOrderInfoLeftBase>
    )
}

interface BuyOrderCreateButtonLogicProps {
    onSubmit: () => void
    OrderId?: string
    status: number
}

const useBuyOrderCreateButtonLogic = ({ onSubmit, OrderId, status }: BuyOrderCreateButtonLogicProps) => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const finalizeOrder = useCallback(async () => {
        startLoading?.()
        await twinFetchPostText('/api/app/order/buy/buyOrder/updateBuyOrder', { id: OrderId, status: status + 1 })
        onSubmit()
        endLoading?.()
    }, [OrderId, onSubmit, status, startLoading, endLoading])

    return { finalizeOrder, loading }
}

interface BuyOrderDeleteButtonProps {
    allRowData: OrderData
    title: string
    id: string
    children?: React.ReactNode
}

const BuyOrderDeleteButton: React.FC<BuyOrderDeleteButtonProps> = ({ allRowData, id, title, children }) => {
    const { openModal, setOpenModal, myOnSubmit } = useBuyOrderDeleteButtonLogic()
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('buy.order')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faTrashCan} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteBuyOrderDraft setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={myOnSubmit} /> : null}
        </PermissionChecker>
)
}

const useBuyOrderDeleteButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'orders')
    
    const myOnSubmit = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])
    return { openModal, setOpenModal, myOnSubmit }
}

export default BuyOrderCreateStepButtons